import type { WorkspaceSocket } from "./sync/shared-client";
import type { BinaryFile, FileContainer } from "@knowledge/smd";

/** The web UI owns presentation. A host replaces only its I/O and navigation. */
export interface WorkspaceRuntime {
  location(): URL;
  navigate(url: string | URL, replace: boolean): void;
  request<T>(
    path: string,
    method: string,
    input?: unknown,
    signal?: AbortSignal,
  ): Promise<T>;
  socket(url: URL | string): WorkspaceSocket;
  widgetSocket(endpoint: string, path: string): Promise<WebSocket>;
  upload(
    file: File,
    container: FileContainer,
    options: {
      purpose?: "comment";
      signal: AbortSignal;
      progress(value: number): void;
      mutationId: string;
    },
  ): Promise<BinaryFile>;
  saved?(pageId: string): void;
  preserveDraft(pageId: string, markdown: string): void;
  openExternal(url: string): void;
}
let runtime: WorkspaceRuntime | undefined;
export function installWorkspaceRuntime(value: WorkspaceRuntime) {
  if (runtime) throw new Error("A workspace runtime is already installed.");
  runtime = value;
  const open = (event: MouseEvent) => {
    if (event.defaultPrevented || !(event.target instanceof Element)) return;
    const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor) return;
    const url = new URL(anchor.getAttribute("href")!, value.location());
    if (!["https:", "http:"].includes(url.protocol)) return;
    event.preventDefault();
    value.openExternal(url.href);
  };
  document.addEventListener("click", open);
  return () => {
    document.removeEventListener("click", open);
    if (runtime === value) runtime = undefined;
  };
}
export const workspaceRuntime = () => runtime;
export const workspaceLocation = (): Location | URL =>
  runtime?.location() ?? globalThis.location;
export function workspaceNavigate(url: string | URL, replace = false) {
  if (runtime) runtime.navigate(url, replace);
  else if (replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
}
export function workspaceExternal(url: string) {
  if (runtime) runtime.openExternal(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}
